import React from 'react'

import { useMediaQueryContext } from 'contexts/MediaQueryContext'

import BaseContainer from 'components/layout/Container'
import InfoParagraph from 'components/landing/InfoParagraph'
import { LargeVideo } from 'components/branding/VideoBanner'
import {
  SpacedColumn,
  ResponsiveTwoColumnLayout,
} from 'components/landing/display'

import AboutRiparide from '../AboutRiparide'
import StoriesIllustrationFigure from '../Intro/StoriesIllustrationFigure'
import Masonry from './ResponsiveMasonry'
import HostInfoCard from './HostInfoCard'
import { ReviewGrid, Review } from './TravelerReviews'
import Header from './Header'
import Signup from './Signup'
import { Subheading, SecondaryHeading, SpacingWrapper } from './styles'

interface LayoutProps {
  cityName: string
  marketName: string
  marketCode: string
  signUpUrl: string
  signUpToken: string
}

const Layout = ({
  cityName,
  marketName,
  marketCode,
  signUpUrl,
  signUpToken,
}: LayoutProps) => {
  const { md: largerThanMd } = useMediaQueryContext()

  return (
    <>
      <Header
        cityName={cityName}
        marketName={marketName}
        signUpUrl={signUpUrl}
        signUpToken={signUpToken}
      />

      <Masonry />

      <BaseContainer width={980}>
        <SecondaryHeading>
          Discover local gems from real travelers
        </SecondaryHeading>

        <Subheading>
          Riparide is the place you can plan and book short nature escapes
          through storyteller travel guides.
        </Subheading>

        <ResponsiveTwoColumnLayout style={{ margin: '48px 0' }}>
          <SpacedColumn>
            <StoriesIllustrationFigure />
          </SpacedColumn>
          <SpacedColumn style={{ paddingTop: '20px' }}>
            <InfoParagraph
              heading="Unlock local experiences"
              text="From hidden glaciers to wilderness cabins, our Riparide Stories help you discover unique mini-vacations in your own backyard."
            />
            <InfoParagraph
              heading="Easy to escape"
              text="Where to go, what to do, where to stay – all our adventure advice comes from real travelers,  making it easy for you to plan and book your next vacation."
            />
            <InfoParagraph
              heading="It’s all social"
              text="Connect with, and be inspired by, other travelers’ adventures and then live out your own epic experience."
            />
          </SpacedColumn>
        </ResponsiveTwoColumnLayout>

        <SpacingWrapper>
          <LargeVideo height={largerThanMd ? '580px' : '400px'} />
        </SpacingWrapper>

        <SecondaryHeading>What our travelers are saying...</SecondaryHeading>

        <ReviewGrid style={{ marginTop: '50px' }}>
          <Review
            name="Chloe"
            text="The property was so unique and aesthetically appealing, and the secluded location was everything you could want in a country escape."
          />
          <Review
            name="Justine"
            text="We especially loved that birdseed was provided for us to become well acquainted with the king parrots on the balcony. They were so friendly and gentle, eating right from our hands. Definitely a highlight of our year."
          />
          <Review
            name="Leo"
            text="Our host checked in on us to make sure we were settled in and just to have a friendly chat. He recommended great places to eat/drink as well as a rundown of the property, which we were able to take in, thanks to the bicycles provided."
          />
          <Review
            name="Ravindi"
            text="We felt like we had escaped the city and had a farm all to ourselves. We enjoyed dinner by the fire and then got cosy with a movie on the projector. And of course, the bath! The photos were good but it was more amazing in real life."
          />
          <Review
            name="Tyson"
            text="We jam-packed our 3-night stay with everything from trout fishing to mountain biking the local trails to hiking the Roof of Australia. We also visited a local vineyard and microbrewery for lunch & beers."
          />
          <Review
            name="Katia"
            text="We went on a lovely walk and swim recommended to us in our Riparide itinerary. We also had a beautiful time wandering up to town for a coffee and just spending time reading in the beautiful tub. It's the perfect place to unwind and rejuvenate."
          />
        </ReviewGrid>

        <SpacingWrapper>
          <ResponsiveTwoColumnLayout gutter="30px">
            <SpacedColumn>
              <HostInfoCard
                marketCode={marketCode}
                marketName={marketName}
                vertical
                inverseColors
              />
            </SpacedColumn>

            <SpacedColumn>
              <AboutRiparide vertical inverseColors noLogo />
            </SpacedColumn>
          </ResponsiveTwoColumnLayout>
        </SpacingWrapper>

        <SecondaryHeading>Ready to escape?</SecondaryHeading>

        <Subheading>
          For news about our 2022 launch in {marketName}, early bookings and
          adventure advice, sign up and stay in the know!
        </Subheading>

        <Signup action={signUpUrl} token={signUpToken} />
      </BaseContainer>
    </>
  )
}

export default Layout
